'use client';

import { useState, useEffect } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import './RevenueLineChart.css';
import { Typography, Select, MenuItem, FormControl } from '@mui/material';
import { axiosInstance } from '../../../../../utils/tokenUtils';
import LoadingScreen from '../../../LoadingScreen/LoadingScreen';
import { Box } from '@mui/material';

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="revenue-custom-tooltip">
        <p className="amount">{`QR ${parseFloat(payload[0].value).toFixed(2)}`}</p>
        <p className="count">{`${payload[0].payload.transactions || 0} transactions`}</p>
      </div>
    );
  }
  return null;
};

// Map time filter to API period parameter
const getApiPeriod = timeFilter => {
  switch (timeFilter) {
    case 'today':
      return 'today';
    case '1week':
      return '1_week';
    case '2week':
      return '2_weeks';
    case '1month':
      return '1_month';
    case '3month':
      return '3_months';
    case '6month':
      return '6_months';
    case '1year':
      return '1_year';
    default:
      return '1_week';
  }
};

// Generate X-axis labels based on period type
const generateXAxisLabels = (periodType, dataPoints) => {
  if (!dataPoints || dataPoints.length === 0) return [];

  switch (periodType) {
    case 'hourly':
      // For today, convert 24-hour format to 12-hour format with AM/PM
      return dataPoints.map(point => {
        const timeStr = point.period; // e.g., "17:00", "19:00"
        const [hour, minute] = timeStr.split(':');
        const hourNum = parseInt(hour);
        const ampm = hourNum >= 12 ? 'PM' : 'AM';
        const hour12 = hourNum === 0 ? 12 : hourNum > 12 ? hourNum - 12 : hourNum;
        return `${hour12} ${ampm}`;
      });

    case 'daily':
      // For daily data, use the period values from API (e.g., "18 Jul", "19 Jul")
      return dataPoints.map(point => point.period);

    case 'weekly':
      // For weekly data, use the period values from API (e.g., "23 Jun", "30 Jun")
      return dataPoints.map(point => point.period);

    case 'monthly':
      // For monthly data, use the period values from API (e.g., "Jan", "Feb")
      return dataPoints.map(point => point.period);

    default:
      return dataPoints.map(point => point.period);
  }
};

// Process API data for chart
const processApiData = apiResponse => {
  if (!apiResponse || !apiResponse.data_points) {
    return { chartData: [], xAxisLabels: [] };
  }

  const { period_type, data_points } = apiResponse;

  // Transform data points for chart
  let chartData = data_points.map(point => {
    let displayPeriod = point.period;

    // For hourly data, convert to 12-hour format for display
    if (period_type === 'hourly') {
      const timeStr = point.period; // e.g., "17:00", "19:00"
      const [hour, minute] = timeStr.split(':');
      const hourNum = parseInt(hour);
      const ampm = hourNum >= 12 ? 'PM' : 'AM';
      const hour12 = hourNum === 0 ? 12 : hourNum > 12 ? hourNum - 12 : hourNum;
      displayPeriod = `${hour12} ${ampm}`;
    }

    return {
      period: displayPeriod,
      originalPeriod: point.period,
      revenue: parseFloat(point.revenue || 0),
      transactions: parseInt(point.transactions || 0),
      date: point.date,
    };
  });

  // Generate X-axis labels based on period type
  let xAxisLabels = generateXAxisLabels(period_type, data_points);

  // --- Ensure last date is today for daily data ---
  if (period_type === 'daily' && chartData.length > 0) {
    const lastDataPoint = chartData[chartData.length - 1];
    // Try to get the last date in YYYY-MM-DD format
    const lastDate = lastDataPoint.date || null;
    const today = new Date();
    const todayStr = today.toISOString().slice(0, 10); // YYYY-MM-DD
    if (lastDate !== todayStr) {
      // Format today's label as in the API (e.g., '21 Jul')
      const todayLabel = `${today.getDate().toString().padStart(2, '0')} ${today.toLocaleString('default', { month: 'short' })}`;
      chartData.push({
        period: todayLabel,
        originalPeriod: todayLabel,
        revenue: 0,
        transactions: 0,
        date: todayStr,
      });
      xAxisLabels.push(todayLabel);
    }
  }
  // --- End ensure last date is today ---

  return { chartData, xAxisLabels, periodType: period_type };
};

const CustomTick = props => {
  const { x, y, payload } = props;

  // Handle multi-line labels (for monthly data with year)
  if (payload.value.includes('\n')) {
    const [month, year] = payload.value.split('\n');
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="middle"
          fill="#64748B"
          fontSize={11}
          fontFamily="Poppins"
        >
          {month}
        </text>
        <text
          x={0}
          y={0}
          dy={28}
          textAnchor="middle"
          fill="#64748B"
          fontSize={11}
          fontFamily="Poppins"
        >
          {year}
        </text>
      </g>
    );
  }

  return (
    <text x={x} y={y + 16} textAnchor="middle" fill="#64748B" fontSize={11} fontFamily="Poppins">
      {payload.value}
    </text>
  );
};

const RevenueLineChart = ({ data }) => {
  const [timeFilter, setTimeFilter] = useState('1week');
  const [chartData, setChartData] = useState([]);
  const [xAxisLabels, setXAxisLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodType, setPeriodType] = useState('daily');
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalTransactions, setTotalTransactions] = useState(0);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        setLoading(true);
        const apiPeriod = getApiPeriod(timeFilter);

        console.log('RevenueLineChart - Fetching data for period:', apiPeriod);

        // Make API call
        const response = await axiosInstance.get(
          `/admin/dashboard/revenue-growth-chart?period=${apiPeriod}`
        );

        console.log('RevenueLineChart - API response:', response.data);

        // Process the API response
        const {
          chartData: processedData,
          xAxisLabels,
          periodType: apiPeriodType,
        } = processApiData(response.data);

        // Set chart data and labels
        setChartData(processedData);
        setXAxisLabels(xAxisLabels);
        setPeriodType(apiPeriodType);

        // Set summary data
        setTotalRevenue(response.data.total_revenue || 0);
        setTotalTransactions(response.data.total_transactions || 0);

        console.log('RevenueLineChart - Processed chart data:', processedData);
        console.log('RevenueLineChart - X-axis labels:', xAxisLabels);
      } catch (error) {
        console.error('Error fetching revenue growth chart:', error);
        // Set empty data on error
        setChartData([]);
        setXAxisLabels([]);
        setTotalRevenue(0);
        setTotalTransactions(0);
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [timeFilter]);

  const handleFilterChange = event => {
    setTimeFilter(event.target.value);
  };

  // Calculate Y-axis domain based on revenue data
  const getYAxisDomain = () => {
    if (!chartData || chartData.length === 0) return [0, 1000];

    const maxRevenue = Math.max(...chartData.map(item => item.revenue));
    const minRevenue = Math.min(...chartData.map(item => item.revenue));

    // If all values are 0, set a reasonable default range
    if (maxRevenue === 0) {
      return [0, 1000];
    }

    // Add some padding to the domain
    const padding = maxRevenue * 0.1;
    return [Math.max(0, minRevenue - padding), maxRevenue + padding];
  };

  return (
    <div className="revenue-analytics-container">
      <div className="revenue-header">
        <Typography className="revenue-header-title">Revenue Growth Chart</Typography>
        <FormControl size="small" style={{ minWidth: 120 }}>
          <Select value={timeFilter} onChange={handleFilterChange} className="revenue-select">
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="1week">1 Week</MenuItem>
            <MenuItem value="2week">2 Weeks</MenuItem>
            <MenuItem value="1month">1 Month</MenuItem>
            <MenuItem value="3month">3 Months</MenuItem>
            <MenuItem value="6month">6 Months</MenuItem>
            <MenuItem value="1year">1 Year</MenuItem>
          </Select>
        </FormControl>
      </div>

      <div className="revenue-chart-container">
        {loading ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}
          >
            <LoadingScreen height={300} width="100%" style={{ borderRadius: 8 }} />
          </Box>
        ) : chartData && chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData} margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.1} />
                  <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0.01} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis
                dataKey="period"
                axisLine={false}
                tickLine={false}
                tick={<CustomTick />}
                interval={xAxisLabels.length > 8 ? 1 : 0}
                minTickGap={xAxisLabels.length > 8 ? 0 : 20}
                ticks={xAxisLabels}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#64748B', fontSize: 12 }}
                tickFormatter={value => `QR ${value}`}
                domain={getYAxisDomain()}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#0EA5E9"
                strokeWidth={2}
                fill="url(#colorRevenue)"
                fillOpacity={1}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 300,
              color: '#64748B',
              fontSize: '14px',
            }}
          >
            No data available for the selected time period
          </Box>
        )}
      </div>

      {/* Summary information */}
      {/* {!loading && chartData && chartData.length > 0 && (
        <div className="revenue-summary">
          <div className="revenue-summary-item">
            <div className="revenue-summary-label">Total Revenue</div>
            <div className="revenue-summary-value">QR {totalRevenue.toFixed(2)}</div>
          </div>
          <div className="revenue-summary-item">
            <div className="revenue-summary-label">Total Transactions</div>
            <div className="revenue-summary-value">{totalTransactions}</div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default RevenueLineChart;
